import React, { useCallback, useEffect, useState } from 'react';
import './AdminOverview.css';
import './AdminFraud.css';
import StatTile from './StatTile';
import RefreshButton from './RefreshButton';
import AccountGraph from './AccountGraph';
import { adminApi } from './utils/adminApi';
import { formatDateTime, formatDate } from './utils/format';

const TYPE_LABELS = { primary: 'Primary (device)', secondary: 'Secondary (browser)', legacy: 'Legacy' };
const TYPE_COLOR = { primary: '#3987e5', secondary: '#9085e9', legacy: '#686868' };

const FLAG_LABELS = {
  primary_fp_match: 'Same device as existing account',
  secondary_fp_match: 'Same browser as existing account',
  ip_match: 'Same IP as existing account',
  signup_after_login: 'Signed up right after a login attempt',
  velocity_block_24h: '3+ signups from this device in 24h',
  device_banned: 'Device was already banned by an admin',
};

const timeAgo = (timestamp) => {
  if (!timestamp) return '-';
  const date = timestamp._seconds ? new Date(timestamp._seconds * 1000) : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '-';
  const secs = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

const TypeBadge = ({ type }) => (
  <span className="admin-badge fraud-type-badge" style={{ color: TYPE_COLOR[type] || '#686868' }}>
    {TYPE_LABELS[type] || type}
  </span>
);

// One connected component: N accounts transitively linked through ANY
// shared fingerprint. Shows the account list, an expandable relationship
// breakdown (which specific fingerprint connects which accounts), and an
// optional inline graph view.
const GroupCard = ({ group, onSelectUser, onChanged }) => {
  const [showLinks, setShowLinks] = useState(false);
  const [showGraph, setShowGraph] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  const unmergeOne = async (hash, uid, type) => {
    if (!window.confirm('Remove this account from this specific fingerprint link?')) return;
    const key = `${hash}:${uid}`;
    setBusyKey(key);
    try {
      await adminApi.unmergeFingerprint({ hash, uid, type });
      onChanged();
    } catch (err) {
      console.error('Unmerge failed:', err);
      alert(err.message || 'Unmerge failed');
    } finally {
      setBusyKey(null);
    }
  };

  const lastSeen = group.links.reduce((max, l) => {
    const t = l.lastSeenAt && (l.lastSeenAt._seconds ? l.lastSeenAt._seconds * 1000 : new Date(l.lastSeenAt).getTime());
    return t && t > max ? t : max;
  }, 0);

  return (
    <div className="fraud-group-card">
      <div className="fraud-group-head">
        <div className="fraud-group-accounts">
          {group.accounts.map((a) => (
            <span key={a.id} className="fraud-account-chip">
              <button className="activity-user-link" onClick={() => onSelectUser && onSelectUser(a.id)}>
                {a.email || a.id}
              </button>
              {a.admin && <span className="admin-badge" style={{ color: '#9085e9' }}>admin</span>}
              {a.deleted && <span className="admin-badge" style={{ color: '#686868' }}>deleted</span>}
            </span>
          ))}
        </div>
        <span className="fraud-group-seen">{group.links.length} link{group.links.length === 1 ? '' : 's'} · last seen {timeAgo(lastSeen)}</span>
      </div>

      <div className="fraud-group-actions">
        <button className="admin-fp-check" onClick={() => setShowLinks((v) => !v)}>
          {showLinks ? 'hide relationships' : 'show relationships'}
        </button>
        <button className="admin-fp-check" onClick={() => setShowGraph((v) => !v)}>
          {showGraph ? 'hide graph' : 'view graph'}
        </button>
      </div>

      {showLinks && (
        <div className="fraud-links-list">
          {group.links.map((link) => (
            <div key={link.hash} className="fraud-link-row">
              <div className="fraud-link-meta">
                <TypeBadge type={link.type} />
                <span className="admin-mono-sm">{link.hash.slice(0, 16)}…</span>
                <span className="fraud-link-device">
                  {[link.device?.browser, link.device?.os].filter(Boolean).join(' · ') || 'Unknown device'}
                </span>
                <span className="fraud-link-seen">{timeAgo(link.lastSeenAt)}</span>
              </div>
              <div className="fraud-link-accounts">
                connects:{' '}
                {link.accounts.map((a, i) => (
                  <span key={a.id} className="fraud-link-account">
                    {i > 0 && ', '}
                    <button className="activity-user-link" onClick={() => onSelectUser && onSelectUser(a.id)}>
                      {a.email || a.id}
                    </button>
                    <button
                      className="fraud-unmerge-btn"
                      disabled={busyKey === `${link.hash}:${a.id}`}
                      onClick={() => unmergeOne(link.hash, a.id, link.type)}
                      title="Remove this account from this fingerprint"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showGraph && (
        <div className="fraud-graph-wrap">
          <AccountGraph accounts={group.accounts} links={group.links} onSelectUser={onSelectUser} size={Math.min(420, 180 + group.accounts.length * 45)} />
        </div>
      )}
    </div>
  );
};

// Detail panel for one fingerprint: full component breakdown + linked
// accounts/visitors + a form to manually merge an account onto it.
const FingerprintDetail = ({ hash, type, onClose, onSelectUser, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [mergeUid, setMergeUid] = useState('');
  const [merging, setMerging] = useState(false);
  const [togglingBan, setTogglingBan] = useState(false);

  useEffect(() => {
    let cancelled = false;
    adminApi.getFingerprintMatches(hash)
      .then((res) => { if (!cancelled) setDetail(res); })
      .catch((err) => console.error('Failed to load fingerprint detail:', err));
    return () => { cancelled = true; };
  }, [hash]);

  const toggleDeviceBan = async () => {
    const nextBanned = !detail.deviceBanned;
    let reason;
    if (nextBanned) {
      reason = window.prompt(
        'Ban this device? Future signups from it will be rejected, and any account that later logs in ' +
        'from it gets auto-suspended.\n\nOptional reason (for your own reference — not shown to users directly):'
      );
      if (reason === null) return; // cancelled
    }
    setTogglingBan(true);
    try {
      await adminApi.banDevice({ hash, banned: nextBanned, reason });
      const res = await adminApi.getFingerprintMatches(hash);
      setDetail(res);
      onChanged && onChanged();
    } catch (err) {
      console.error('Toggle device ban failed:', err);
      alert(err.message || 'Failed to update device ban');
    } finally {
      setTogglingBan(false);
    }
  };

  const merge = async () => {
    const uid = mergeUid.trim();
    if (!uid) return;
    setMerging(true);
    try {
      await adminApi.mergeFingerprint({ hash, uid, type: type || 'legacy' });
      setMergeUid('');
      const res = await adminApi.getFingerprintMatches(hash);
      setDetail(res);
      onChanged && onChanged();
    } catch (err) {
      console.error('Merge failed:', err);
      alert(err.message || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  return (
    <>
      <div className="admin-detail-overlay" onClick={onClose} />
      <div className="admin-detail-panel open fraud-detail-panel">
        <div className="admin-detail-header">
          <div className="admin-detail-title admin-mono-sm">{hash}</div>
          <button className="admin-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="admin-detail-body">
          {!detail ? (
            <div className="admin-loading">Loading...</div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <TypeBadge type={detail.type || type || 'legacy'} />
                {detail.deviceBanned && <span className="admin-badge" style={{ color: '#ef4444' }}>device banned</span>}
                {(detail.type || type) === 'primary' && (
                  <button className="admin-fp-check" disabled={togglingBan} onClick={toggleDeviceBan}>
                    {togglingBan ? 'updating…' : detail.deviceBanned ? 'unban device' : 'ban device'}
                  </button>
                )}
              </div>
              <div className="admin-detail-row"><span className="admin-detail-row-label">Device</span><span className="admin-detail-row-value">{[detail.device?.browser, detail.device?.os].filter(Boolean).join(' / ') || '—'}</span></div>
              <div className="admin-detail-row"><span className="admin-detail-row-label">Last IP</span><span className="admin-detail-row-value admin-mono">{detail.lastIp || '—'}</span></div>
              <div className="admin-detail-row"><span className="admin-detail-row-label">First / last seen</span><span className="admin-detail-row-value">{formatDate(detail.firstSeenAt)} → {formatDate(detail.lastSeenAt)}</span></div>

              {detail.components && (
                <div className="admin-fp-components" style={{ marginTop: 10 }}>
                  {Object.entries(detail.components).map(([key, value]) => (
                    <div key={key} className="admin-fp-component">
                      <span>{key}</span>
                      <span className="admin-mono">{typeof value === 'boolean' ? (value ? 'yes' : 'no') : String(value)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="admin-subblock-title" style={{ marginTop: 14 }}>Linked accounts ({detail.users?.length || 0})</div>
              <div className="fraud-linked-accounts">
                {(detail.users || []).length === 0 ? (
                  <div className="no-data">No accounts linked yet</div>
                ) : (
                  detail.users.map((u) => (
                    <span key={u.id} className="fraud-account-chip">
                      <button className="activity-user-link" onClick={() => onSelectUser && onSelectUser(u.id)}>{u.email || u.id}</button>
                      {u.admin && <span className="admin-badge" style={{ color: '#9085e9' }}>admin</span>}
                    </span>
                  ))
                )}
              </div>
              {(detail.visitorIds || []).length > 0 && (
                <div className="admin-fp-meta" style={{ marginTop: 6 }}>{detail.visitorIds.length} anonymous visitor(s) also seen with this fingerprint</div>
              )}

              <div className="fraud-merge-row" style={{ marginTop: 14 }}>
                <input type="text" placeholder="Account UID to merge onto this fingerprint..." value={mergeUid} onChange={(e) => setMergeUid(e.target.value)} />
                <button className="admin-btn admin-btn-secondary" disabled={!mergeUid.trim() || merging} onClick={merge}>
                  {merging ? 'Merging…' : 'Merge'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

// Search + paginated browser over every fingerprint ever seen.
const FingerprintBrowser = ({ onSelectUser, onChanged }) => {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (append = false) => {
    setLoading(true);
    try {
      const res = await adminApi.browseFingerprints({
        search: search.trim() || undefined,
        type: typeFilter || undefined,
        cursor: append ? cursor : undefined,
      });
      setRows((prev) => (append ? [...prev, ...(res.fingerprints || [])] : (res.fingerprints || [])));
      setCursor(res.nextCursor);
      setHasMore(Boolean(res.hasMore));
    } catch (err) {
      console.error('Failed to browse fingerprints:', err);
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, cursor]);

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter]);

  const runSearch = (e) => {
    e.preventDefault();
    load(false);
  };

  return (
    <div className="admin-section">
      <div className="admin-block-head">
        <h2>Browse &amp; Search Fingerprints</h2>
        <RefreshButton onRefresh={() => load(false)} title="Refresh fingerprints" />
      </div>

      <form className="admin-search-form" onSubmit={runSearch} style={{ marginBottom: 10 }}>
        <input
          type="text"
          placeholder="Search by hash prefix or exact account email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="submit" className="admin-btn admin-btn-secondary">Search</button>
        <select className="admin-filter-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          <option value="primary">Primary</option>
          <option value="secondary">Secondary</option>
        </select>
      </form>

      {rows.length === 0 && !loading ? (
        <div className="no-data">No fingerprints found</div>
      ) : (
        <div className="fraud-fp-table-wrap">
          <table className="activity-errors-table">
            <thead>
              <tr>
                <th>Hash</th>
                <th>Type</th>
                <th>Device</th>
                <th>Accounts</th>
                <th>Visitors</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.hash} className="fraud-fp-row" onClick={() => setSelected(r)}>
                  <td className="admin-mono-sm">{r.hash.slice(0, 20)}…</td>
                  <td><TypeBadge type={r.type} /></td>
                  <td>{[r.device?.browser, r.device?.os].filter(Boolean).join(' · ') || '—'}</td>
                  <td>{r.accountCount}</td>
                  <td>{r.visitorCount}</td>
                  <td>{timeAgo(r.lastSeenAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loading && <div className="admin-loading">Loading...</div>}
      {hasMore && !loading && (
        <button className="admin-btn admin-btn-secondary" style={{ marginTop: 10 }} onClick={() => load(true)}>
          Load more
        </button>
      )}

      {selected && (
        <FingerprintDetail
          hash={selected.hash}
          type={selected.type}
          onClose={() => setSelected(null)}
          onSelectUser={onSelectUser}
          onChanged={() => { load(false); onChanged && onChanged(); }}
        />
      )}
    </div>
  );
};

const AdminFraud = ({ onSelectUser }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState({ totalSignups: 0, blocked: 0, flagged: 0, multiAccountGroups: 0 });
  const [groups, setGroups] = useState([]);
  const [flagged, setFlagged] = useState([]);

  const loadStats = useCallback(async (numDays) => {
    const res = await adminApi.getFraudStats(numDays);
    setStats(res);
  }, []);

  const loadGroups = useCallback(async () => {
    const res = await adminApi.getFraudGroups();
    setGroups(res.groups || []);
  }, []);

  const loadFlagged = useCallback(async () => {
    const res = await adminApi.getFlaggedSignups({ limit: 50 });
    setFlagged(res.flagged || []);
  }, []);

  const loadAll = useCallback(async (numDays, soft = false) => {
    if (!soft) setLoading(true);
    setError('');
    try {
      await Promise.all([loadStats(numDays), loadGroups(), loadFlagged()]);
    } catch (err) {
      console.error('Error loading fraud data:', err);
      setError(err.message || 'Failed to load fraud data');
    } finally {
      setLoading(false);
    }
  }, [loadStats, loadGroups, loadFlagged]);

  useEffect(() => {
    loadAll(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  if (loading) return <div className="admin-loading">Loading...</div>;
  if (error) return <div className="admin-error">{error}</div>;

  return (
    <div>
      <div className="fraud-toolbar">
        <div className="activity-range-buttons">
          {[7, 30, 90].map((n) => (
            <button key={n} className={`admin-tab ${days === n ? 'active' : ''}`} onClick={() => setDays(n)}>
              {n}d
            </button>
          ))}
        </div>
        <RefreshButton onRefresh={() => loadAll(days, true)} title="Refresh fraud data" />
      </div>

      <div className="fraud-hint">
        Nothing here blocks a signup automatically — scores and matches are for manual review only. The one automatic
        rule is 3+ signups from the same device within 24h, which is rejected outright to protect the referral system.
        Groups below are transitive: if account A shares a device with B, and B (on a different device) shares with C,
        A/B/C all show as one group even though A and C never shared a device directly.
      </div>

      <div className="admin-stats-grid">
        <StatTile label={`Signups (${days}d)`} value={stats.totalSignups} />
        <StatTile label="Blocked (velocity rule)" value={stats.blocked} accent="#ef4444" />
        <StatTile label="Flagged for review" value={stats.flagged} accent="#eab308" />
        <StatTile label="Multi-account groups" value={stats.multiAccountGroups} accent="#9085e9" />
      </div>

      <div className="admin-section">
        <div className="admin-block-head">
          <h2>Multi-Account Groups</h2>
          <RefreshButton onRefresh={loadGroups} title="Refresh groups" />
        </div>
        {groups.length === 0 ? (
          <div className="no-data">No accounts are currently connected through a shared fingerprint</div>
        ) : (
          <div className="fraud-groups-list">
            {groups.map((g) => (
              <GroupCard key={g.id} group={g} onSelectUser={onSelectUser} onChanged={loadGroups} />
            ))}
          </div>
        )}
      </div>

      <FingerprintBrowser onSelectUser={onSelectUser} onChanged={loadGroups} />

      <div className="admin-section">
        <div className="admin-block-head">
          <h2>Flagged Signups</h2>
          <RefreshButton onRefresh={loadFlagged} title="Refresh flagged signups" />
        </div>
        {flagged.length === 0 ? (
          <div className="no-data">No flagged or blocked signups</div>
        ) : (
          <div className="activity-errors-table-wrap">
            <table className="activity-errors-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Email</th>
                  <th>Score</th>
                  <th>Flags</th>
                  <th>IP</th>
                  <th>Outcome</th>
                  <th>Account</th>
                </tr>
              </thead>
              <tbody>
                {flagged.map((f) => (
                  <tr key={f.id}>
                    <td>{formatDateTime(f.ts)}</td>
                    <td>{f.email || '-'}</td>
                    <td>{f.score ?? '-'}</td>
                    <td>
                      {(f.flags || []).map((flag) => (
                        <span key={flag} className="admin-badge fraud-flag-badge" style={{ color: '#eab308' }}>
                          {FLAG_LABELS[flag] || flag}
                        </span>
                      ))}
                    </td>
                    <td className="admin-mono-sm">{f.ip || '-'}</td>
                    <td>
                      <span className="admin-badge" style={{ color: f.outcome === 'blocked' ? '#ef4444' : '#22c55e' }}>
                        {f.outcome}
                      </span>
                    </td>
                    <td>
                      {f.uid ? (
                        <button className="activity-user-link" onClick={() => onSelectUser && onSelectUser(f.uid)}>account</button>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFraud;
